import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Sparkles, Video, CheckCircle2, Eye, ArrowRight, Star } from "lucide-react";
import { Logo } from "../components/Brand";
import { useAuth } from "../context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1758612897940-0996f3f58b43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CRAFT = "https://images.unsplash.com/photo-1714646793449-6967987cfcae?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const features = [
  { icon: CheckCircle2, title: "You approve everything", body: "Every video and every comment waits for a grown-up's thumbs up before anyone else sees it.", bg: "#FFD23F" },
  { icon: Sparkles, title: "AI safety buddy", body: "Titles, comments and every video are checked for unkind words, unsafe images and personal info before they even reach you.", bg: "#3DCCC7" },
  { icon: Clock, title: "Screen-time that sticks", body: "Set a daily minute budget per kid. When it's done, Zunii kindly says goodnight.", bg: "#FF8FC7" },
  { icon: Eye, title: "Activity at a glance", body: "A friendly timeline of uploads, comments, logins and anything our AI blocked.", bg: "#B8F2C9" },
];

export default function Landing() {
  const { parent } = useAuth();
  const cta = parent ? "/profiles" : "/signup";
  return (
    <div className="min-h-screen bg-[#FFF7EC] overflow-hidden">
      <header className="max-w-7xl mx-auto px-4 sm:px-8 h-20 sm:h-24 flex items-center justify-between gap-3 mt-[env(safe-area-inset-top)]">
        <Logo className="text-2xl sm:text-3xl" />
        <div className="flex gap-2 sm:gap-3">
          {!parent && <Link to="/login" data-testid="landing-login-btn" className="zn-btn bg-white !py-2 !px-4 text-sm sm:text-base">Log in</Link>}
          <Link to={cta} data-testid="landing-header-cta" className="zn-btn bg-[#FF6B4A] text-white !py-2 !px-4 text-sm sm:text-base">{parent ? "Open Zunii" : "Get started"}</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-4 sm:pt-8 pb-16 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 items-center">
        <div className="lg:col-span-7 zn-pop">
          <span className="zn-chip bg-[#B8F2C9] mb-5 sm:mb-6"><ShieldCheck size={16} /> Parent-supervised, always</span>
          <h1 className="text-[2.6rem] sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight mb-5 sm:mb-6">
            Where kids make <span className="relative inline-block px-2"><span className="absolute inset-0 bg-[#FFD23F] -rotate-2 rounded-xl border-[3px] border-[#1B1A2E]" /><span className="relative">big ideas</span></span> into little videos.
          </h1>
          <p className="text-base sm:text-lg text-[#1B1A2E]/75 max-w-xl mb-7 sm:mb-10">
            Zunii is a cozy creative video community for kids 5–13. They record, share and cheer each other on. You stay in charge of every single thing that goes public.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link to={cta} data-testid="landing-hero-cta" className="zn-btn bg-[#7B5CFA] text-white text-lg">Create a family account <ArrowRight size={20} /></Link>
            <a href="#how" data-testid="landing-how-link" className="zn-btn bg-white text-lg">How it works</a>
          </div>
        </div>
        <div className="lg:col-span-5 relative mx-2 sm:mx-0">
          <div className="zn-card overflow-hidden rotate-2 zn-float">
            <img src={HERO} srcSet={`${HERO.replace("w=1200", "w=640")} 640w, ${HERO} 1200w`} sizes="(max-width: 1024px) 92vw, 40vw" alt="Happy kids filming a video" fetchPriority="high" className="w-full aspect-[4/3] lg:aspect-[4/5] object-cover" />
          </div>
          <div className="zn-card absolute left-2 sm:-left-8 -bottom-5 sm:bottom-10 p-3 sm:p-4 bg-[#FFD23F] -rotate-3 flex items-center gap-3">
            <Video size={22} />
            <div><p className="font-display font-semibold leading-none">"My Lego Rocket"</p><p className="text-xs mt-1">Approved by Mom · 12 cheers</p></div>
          </div>
          <div className="absolute -top-5 right-1 sm:-top-6 sm:-right-4 w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-[#FF6B4A] border-[3px] border-[#1B1A2E] grid place-items-center zn-wiggle">
            <Star fill="#FFD23F" className="w-6 h-6 sm:w-[34px] sm:h-[34px]" />
          </div>
        </div>
      </section>

      <section id="how" className="bg-[#1B1A2E] text-white py-14 sm:py-20 scroll-mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <h2 className="text-3xl sm:text-5xl font-bold mb-3 max-w-2xl">Built for kids. Controlled by grown-ups.</h2>
          <p className="text-white/70 mb-8 sm:mb-12 max-w-xl">Four simple promises we keep for every family on Zunii.</p>
          <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-3" data-testid="feature-carousel">
            {features.map(({ icon: Icon, title, body, bg }, i) => (
              <div key={title} data-testid={`feature-card-${i}`} className="zn-card zn-pop p-6 sm:p-7 text-[#1B1A2E] shadow-[5px_5px_0_0_#FFF7EC] snap-start shrink-0 w-[80%] md:w-auto" style={{ background: bg, animationDelay: `${i * 90}ms` }}>
                <Icon size={32} strokeWidth={2.5} className="mb-4 sm:mb-5" />
                <h3 className="text-xl sm:text-2xl font-semibold mb-2">{title}</h3>
                <p className="text-[#1B1A2E]/80">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20 grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
        <div className="zn-card overflow-hidden -rotate-1"><img src={CRAFT} srcSet={`${CRAFT.replace("w=900", "w=640")} 640w, ${CRAFT} 900w`} sizes="(max-width: 768px) 92vw, 45vw" loading="lazy" alt="Kids crafting" className="w-full aspect-video object-cover" /></div>
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-5 sm:mb-6">Three steps to their first video</h2>
          {["Create your parent account", "Add kid profiles with a secret 4-digit PIN", "Kids record or upload — you approve"].map((s, i) => (
            <div key={s} className="flex items-center gap-4 mb-4">
              <span className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-full bg-[#FF6B4A] text-white font-display font-bold text-xl grid place-items-center border-[3px] border-[#1B1A2E]">{i + 1}</span>
              <p className="text-base sm:text-lg font-semibold">{s}</p>
            </div>
          ))}
          <Link to={cta} data-testid="landing-bottom-cta" className="zn-btn bg-[#FFD23F] mt-6 w-full sm:w-auto">Start free <ArrowRight size={18} /></Link>
        </div>
      </section>
      <footer className="border-t-[3px] border-[#1B1A2E] py-8 pb-[calc(2rem+env(safe-area-inset-bottom))] text-center text-sm text-[#1B1A2E]/60 space-y-2">
        <p>Zunii — a safe stage for small creators.</p>
        <p className="flex justify-center gap-4">
          <Link to="/privacy" data-testid="footer-privacy-link" className="underline">Privacy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="underline">Terms</Link>
          <Link to="/safety" data-testid="footer-safety-link" className="underline">Child Safety</Link>
        </p>
      </footer>
    </div>
  );
}
